import React, { useState, useMemo, useEffect, useRef, useContext, act } from 'react';
import { PostCallWithErrorResponse, simpleGetCall } from '../Api/ApiServices';
import { ApiConfig } from '../Api/ApiConfig';
import { AppContext } from '../context/AppContext';
import ReactMarkdown from 'react-markdown';
import CustomSelect from '../constant/customselect';
import { Form } from 'react-bootstrap';


/**
 * MOCK DATA: JOBS & CANDIDATES
 */
const MOCK_JOBS = [
  { id: 'J-101', title: 'Senior React Developer', description: 'Requires 5+ years of React experience, deep knowledge of hooks, and performance optimization. Must have experience with Redux and Node.js. strict compliance with WCAG 2.1 is required.' },
  { id: 'J-102', title: 'Product Manager', description: 'Seeking a PM with experience in AI-driven tools and cross-functional team leadership. Must have owned a product roadmap from 0 to 1.' },
  { id: 'J-103', title: 'UX Designer', description: 'Focus on accessibility, design systems, and rapid prototyping in Figma. Experience with user research and usability testing is mandatory.' },
  { id: 'J-104', title: 'Data Scientist', description: 'Experience with Python, PyTorch, and large language models. PhD preferred.' },
  { id: 'J-105', title: 'DevOps Engineer', description: 'Strong background in AWS, Kubernetes, and CI/CD pipelines. Terraform expertise required.' }
];


const MOCK_CANDIDATES = [
  { id: 'C-01', jobId: 'J-101', name: 'Alice Chen', experience: '6 years', skills: 'React, Redux, Node.js' },
  { id: 'C-02', jobId: 'J-101', name: 'Bob Smith', experience: '4 years', skills: 'React, TypeScript, CSS' },
  { id: 'C-03', jobId: 'J-102', name: 'Charlie Brown', experience: '7 years', skills: 'Agile, Roadmap Planning, Python' },
  { id: 'C-04', jobId: 'J-103', name: 'Diana Prince', experience: '5 years', skills: 'Figma, Design Systems, WCAG' },
  { id: 'C-05', jobId: 'J-104', name: 'Evan Wright', experience: '3 years', skills: 'Python, R, SQL' },
  { id: 'C-06', jobId: 'J-105', name: 'Fiona Gallagher', experience: '8 years', skills: 'AWS, Docker, Jenkins' }
];


const AVAILABLE_MODELS = [
  { id: 'gpt-4o', name: 'GPT-4o', provider: 'OpenAI' },
  { id: 'gpt-4.1', name: 'GPT-4.1', provider: 'OpenAI' },
  { id: 'deepseek-reasoner', name: 'DeepSeek Reasoner', provider: 'DeepSeek' },
  { id: 'gemini-2.0-flash', name: 'Gemini 2.0 Flash', provider: 'Google' },
  { id: 'gemini-2.5-pro', name: 'Gemini 2.5 Pro', provider: 'Google' },
  { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash', provider: 'Google' },
  { id: 'fireworks/deepseek-r1-0528', name: 'DeepSeek R1 (Fireworks)', provider: 'Fireworks AI' },
  { id: 'llama-v3p3-70b-instruct', name: 'Llama 3.3 70B', provider: 'Meta' },
  { id: 'fireworks/deepseek-v3p1', name: 'DeepSeek V3.1 (Fireworks)', provider: 'Fireworks AI' }
];


const COMPONENT_TYPES = [
  { id: 'MARKDOWN', name: 'Rich Text / Markdown' },
  { id: 'TABLE', name: 'Data Table' },
  { id: 'CHART', name: 'Visual Chart' },
  { id: 'JSON', name: 'Raw JSON' }
];


const INITIAL_PROMPTS = [
  {
    id: 'p1',
    title: 'Candidate Fit Score',
    content: 'Assess how well {{candidate_name}} matches the requirements for the job: "{{job_description}}". Provide a compatibility score out of 10.',
    selectedModels: ['gpt-4o', 'gemini-2.5-pro'],
    uiType: 'MARKDOWN',
    winnerModelId: null
  },
  {
    id: 'p2',
    title: 'Interview Question Generator',
    content: 'Based on the JD: "{{job_description}}", generate 3 technical interview questions for {{candidate_name}}.',
    selectedModels: ['gpt-4o'],
    uiType: 'TABLE',
    winnerModelId: null
  },
  {
    id: 'p3',
    title: 'Screening Question Generator',
    content: `You are a recruitment and hiring specialist. Carefully read and analyze the Job Description (JD): "{{job_description}}". Gain a clear understanding of the company’s business, role expectations, key responsibilities, required skills, compliance requirements, and overall hiring intent. Based strictly on this analysis, generate 12 custom screening questions for use in the initial candidate screening stage.


Question Requirements:
- All questions must be answerable strictly as “Yes” or “No.”
- Questions must be job-specific and directly derived from the JD (avoid generic questions).
- Each question should help assess candidate experience, technical capability, compliance readiness, role fit, or ownership level.
- Questions should be clearly judgeable for shortlisting during preliminary screening.
- Avoid open-ended, subjective, or opinion-based questions.


Ranking & Categorization Instructions:
- Rank and sort the 12 questions based on their importance and relevance, using the candidate’s experience level and criticality to the role as key parameters.
- Categorize the questions into two sections: "Strongly Recommended Questions" and "Recommended Questions".
- Exactly 50% of the total questions (6 out of 12) must be categorized as Strongly Recommended.
- The remaining questions must fall under Recommended only.


Justification Requirement:
- Provide a brief justification (1–2 lines each) explaining why the Strongly Recommended questions are critical for evaluating suitability for this role, based on the JD analysis.


Output Format:
Section 1: Strongly Recommended Questions (Ranked 1–6)
Question: [Question text]
Justification: [Reasoning]


Section 2: Recommended Questions (Ranked 7–12)
Question: [Question text]


Ensure the wording is clear, concise, unambiguous, and aligned strictly with the JD.`,
    selectedModels: ['gpt-4o', 'gemini-2.5-pro'],
    uiType: 'MARKDOWN',
    winnerModelId: null
  },
  {
    id: 'p4',
    title: 'Work Style & Personality Analysis',
    content: `ANALYSIS FRAMEWORK:
1. Examine career trajectory: promotions, role changes, tenure patterns
2. Analyze language: action verbs, adjectives, tone, self-description
3. Review achievements: scope, quantifiable results, problem-solving approach
4. Assess skills: breadth vs. depth, soft skills, continuous learning
5. Consider extracurriculars: passions, values, outside interests


OUTPUT REQUIREMENTS:
- Generate EXACTLY 5 bullet points
- Each point must describe a personality trait or work style
- Include brief rationale from resume evidence
- Address: working capacity, adaptability, task willingness, collaboration style
- Format: "• [Trait] - [Brief evidence/explanation]"
- Focus on HOW they work, not WHAT they've done
- Professional, insightful tone


Context: Analyze {{candidate_name}} based on their resume/profile data.`,
    selectedModels: ['gpt-4o', 'claude-3-5-sonnet'],
    uiType: 'MARKDOWN',
    winnerModelId: null
  }
];


/**
 * REUSABLE AUTOCOMPLETE SELECT COMPONENT
 */
const AutocompleteSelect = ({ label, options, value, onChange, placeholder, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const wrapperRef = useRef(null);


  useEffect(() => {
    const selectedOption = options.find(o => o.value === value);
    if (selectedOption) {
      setInputValue(selectedOption.label);
    } else if (value === '') {
      setInputValue('');
    }
  }, [value, options]);


  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        const selectedOption = options.find(o => o.value === value);
        if (selectedOption) {
          setInputValue(selectedOption.label);
        } else if (value === '') {
          setInputValue('');
        }
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [wrapperRef, value, options]);


  const filteredOptions = options.filter(opt =>
    opt.label.toLowerCase().includes(inputValue.toLowerCase())
  );


  const handleSelect = (option) => {
    onChange(option.value);
    setInputValue(option.label);
    setIsOpen(false);
  };


  const handleInputChange = (e) => {
    const newVal = e.target.value;
    setInputValue(newVal);
    setIsOpen(true);
    if (newVal === '') {
      onChange('');
    }
  };


  return (
    <div className="mb-0 position-relative" ref={wrapperRef}>
      <label className="form-label text-base fw-bold">{label}</label>
      <div className="input-group input-group-sm">
        <input
          type="text"
          className="form-control text-sm shadow-none"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          disabled={disabled}
          autoComplete="off"
        />
        <span className="input-group-text bg-white border-start-0 text-muted">▼</span>
      </div>

      {isOpen && !disabled && (
        <div className="position-absolute w-100 bg-white border rounded shadow-lg mt-1 overflow-auto" style={{ zIndex: 1050, maxHeight: '200px' }}>
          {filteredOptions.length > 0 ? (
            filteredOptions.map(opt => (
              <div
                key={opt.value}
                className={`px-3 py-2 text-sm border-bottom border-light ${opt.value === value ? 'bg-primary text-white' : 'text-dark'}`}
                style={{ cursor: 'pointer' }}
                onClick={() => handleSelect(opt)}
                onMouseEnter={(e) => {
                  if (opt.value !== value) e.currentTarget.style.backgroundColor = '#f8f9fa';
                }}
                onMouseLeave={(e) => {
                  if (opt.value !== value) e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                {opt.label}
              </div>
            ))
          ) : (
            <div className="px-3 py-2 text-sm text-muted fst-italic">No matches found</div>
          )}
        </div>
      )}
    </div>
  );
};


/**
 * COMPONENT REGISTRY
 */
const RenderResult = ({ type, data, isLoading }) => {
  if (isLoading) {
    return (
      <div className="d-flex justify-content-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }


  if (!data) return <div className="text-muted p-4 fst-italic">Execute to see results...</div>;


  // switch (type) {
  //   case 'TABLE':
  //     const tableData = Array.isArray(data) ? data : [];
  //     return (
  //       <div className="table-responsive">
  //         <table className="table table-sm border-0 m-0">
  //           <thead className="table-light">
  //             <tr>
  //               {Object.keys(tableData[0] || {}).map(k => (
  //                 <th key={k} className="text-xs fw-bold text-uppercase">{k}</th>
  //               ))}
  //             </tr>
  //           </thead>
  //           <tbody>
  //             {tableData.map((row, i) => (
  //               <tr key={i}>
  //                 {Object.values(row).map((v, j) => (
  //                   <td key={j} className="text-sm">
  //                     {typeof v === 'object' ? JSON.stringify(v) : String(v)}
  //                   </td>
  //                 ))}
  //               </tr>
  //             ))}
  //           </tbody>
  //         </table>
  //       </div>
  //     );
  //   case 'CHART':
  //     const chartData = Array.isArray(data) ? data : [];
  //     return (
  //       <div className="p-4 text-center bg-white">
  //         <div className="mb-3 fw-bold text-muted text-xs text-uppercase" style={{ letterSpacing: '0.05em' }}>Analysis Visualization</div>
  //         <div className="d-flex align-items-end justify-content-center gap-3" style={{ height: '160px' }}>
  //           {chartData.map((item, i) => (
  //             <div key={i} className="text-center w-100" style={{ maxWidth: '32px' }}>
  //               <div
  //                 className="bg-primary rounded-top mx-auto"
  //                 style={{ height: `${item.value || 0}%`, transition: 'height 0.6s ease-out' }}
  //                 title={`${item.label}: ${item.value}`}
  //               ></div>
  //               <div className="mt-2 text-xs text-muted font-monospace">{String(item.label || '')}</div>
  //             </div>
  //           ))}
  //         </div>
  //       </div>
  //     );
  //   case 'JSON':
  //     return (
  //       <div className="p-3 bg-dark rounded-bottom">
  //         <pre className="text-xs text-info m-0" style={{ whiteSpace: 'pre-wrap' }}>
  //           {JSON.stringify(data, null, 2)}
  //         </pre>
  //       </div>
  //     );
  //   default:
  //     let content = typeof data === 'string' ? data : (data?.text || JSON.stringify(data));
  //     return (
  //       <div className="p-4 border-start border-4 border-primary bg-white">
  //         <div className="m-0 text-sm text-dark" style={{ lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{content}</div>
  //       </div>
  //     );
  // }

  return (
    <div className="p-4 border-start border-4 border-primary bg-white">
      {/* <div className="m-0 text-sm text-dark" style={{ lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{data}</div> */}
      <ReactMarkdown >{data}</ReactMarkdown>
    </div>
  );
};


/**
 * MAIN APP COMPONENT
 */
export default function PromptWorkbench() {
  const [prompts, setPrompts] = useState(INITIAL_PROMPTS);
  const [activePrompt, setActivePrompt] = useState(INITIAL_PROMPTS[0]);
  const [results, setResults] = useState({});
  const [executing, setExecuting] = useState(false);
  const [showToast, setShowToast] = useState(null);
  const [showFeatureModal, setShowFeatureModal] = useState(false);

  const [selectedWinnerId, setSelectedWinnerId] = useState(null);
  const [showModelSelector, setShowModelSelector] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingModelId, setPendingModelId] = useState(null);
  const [activeResultTab, setActiveResultTab] = useState(null);
  const [suggestedPrompt, setSuggestedPrompt] = useState(null);
  const [search, setSearch] = useState('');

  const [codeList, setCodeList] = useState([])

  const [candidates, setCandidates] = useState([])

  const [aiModels, setAiModels] = useState([])

  const [defaultVar, setDefualtVar] = useState({
    code: '',
    var: []
  })


  const { adminData, companyDtls } = useContext(AppContext);



  const [selectedJobId, setSelectedJobId] = useState('');
  const [selectedCandidateId, setSelectedCandidateId] = useState('');


  const filteredCandidates = useMemo(() =>
    MOCK_CANDIDATES.filter(c => c.jobId === selectedJobId),
    [selectedJobId]
  );



  const activeJob = MOCK_JOBS.find(j => j.id === selectedJobId);
  const activeCandidate = MOCK_CANDIDATES.find(c => c.id === selectedCandidateId);

  const getSuggestedPrompt = () => {

    simpleGetCall(ApiConfig.SUGGESTED_PROMPT).then(res => {

      if (res?.succeded) {

        setActivePrompt(res?.response[0])
        setSuggestedPrompt(res?.response)
      }

    })

  }

  const getAiModels = () => {

    simpleGetCall(ApiConfig.GET_AI_MODELS).then(res => {

      if (res?.succeded) {

        setAiModels(res?.response)
      }

    })

  }


  const getJobCodesList = () => {

    simpleGetCall(ApiConfig.GROUP_LIST + `${adminData?.email}&IsActive=true`).then((res) => {

      if (res?.succeded) {
        setCodeList(res?.response)
      }

    })

  }



  const candidateList = (code) => {


    PostCallWithErrorResponse(ApiConfig.ACCESS_CODE_LIST + `code=${code}&export=false&isActive=true&email=${adminData?.email}`, {}).then((res) => {

      if (res?.json?.succeded) {
        setCandidates(res?.json?.response)
      } else {
        setCandidates([])
      }

    })

  }


  useEffect(() => {
    getAiModels()
    getSuggestedPrompt()
    getJobCodesList()
  }, [])


  useEffect(() => {
  let bootstrapLink = document.getElementById("bootstrap-css");
  let interFontLink = document.getElementById("inter-font");

  if (!bootstrapLink) {
    bootstrapLink = document.createElement("link");
    bootstrapLink.id = "bootstrap-css";
    bootstrapLink.rel = "stylesheet";
    bootstrapLink.href =
      "https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css";

    document.head.appendChild(bootstrapLink);
  }

  if (!interFontLink) {
    interFontLink = document.createElement("link");
    interFontLink.id = "inter-font";
    interFontLink.rel = "stylesheet";
    interFontLink.href =
      "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap";

    document.head.appendChild(interFontLink);
  }

  return () => {
    bootstrapLink?.remove();
    interFontLink?.remove();
  };
}, []);


  useEffect(() => {
    if (activePrompt?.selectedModels?.length > 0) {
      if (!activeResultTab || !activePrompt.selectedModels.includes(activeResultTab)) {
        setActiveResultTab(activePrompt.selectedModels[0]);
      }
    } else {
      setActiveResultTab(null);
    }
  }, [activePrompt.selectedModels, activeResultTab]);


  const updateActivePrompt = (fields) => {
    setActivePrompt(prev => ({ ...prev, ...fields }));
  };




  const getDefualtValueForvar = (code, userId = 0) => {



    // if (!selectedJobId) return

    simpleGetCall(ApiConfig.GET_DEFAuLT_VAR_VALUES + `Code=${code}&PromptId=${activePrompt?.id}&userId=${userId}`).then((res) => {

      if (res?.succeded) {
        setDefualtVar({
          code: code,
          defaultVar: res?.response
        })


        res?.response?.forEach((i) => {

          setActivePrompt(prev => ({
            ...prev,
            variables: prev?.variables?.map(v => v.name === i.name ? { ...v, defaultValue: i.value } : v)
          }))

        })


      }

    })

  }



  const handleExecute = async () => {
    setExecuting(true);
    setSelectedWinnerId(null);

    if (activePrompt?.selectedModels?.length < 0) {
      setActiveResultTab(activePrompt?.selectedModels[0] || []);
    }


    let input_variables = {}
    activePrompt?.variables?.forEach((i) => {
      input_variables[i?.name] = input_variables[i?.name] === "format_instructions" ? "" : i?.defaultValue
    })

    setActivePrompt(prev => ({
      ...prev,
      isPromptLoading: true
    }))


    const model = activePrompt?.selectedModels?.map((modelId => aiModels.find(m => m.id === modelId)))?.map((i) => i?.modelName);
    PostCallWithErrorResponse(ApiConfig.GENERATE_RESPONSE_FROM_AI, {
      input_variables,
      prompt_template: activePrompt?.versions && activePrompt?.versions[0]?.templateBody || '',
      model_names: model
    }).then((res) => {

      if (res?.json?.status == 200) {
        setExecuting(false)
        setActivePrompt(prev => ({
          ...prev,
          result: res?.json?.results,
          isPromptLoading: false
        }))
      }


    })


    console.log(input_variables)



    // const newResults = {};
    // for (const modelId of activePrompt.selectedModels || []) {
    //   newResults[modelId] = { loading: true };
    // }
    // setResults(newResults);


    // for (const modelId of activePrompt.selectedModels || []) {


    //   console.log(activePrompt);


    //   // PostCallWithErrorResponse(ApiConfig.GENERATE_PROMPT_RESPONSE, {
    //   //   promptId: activePrompt.id,
    //   //   systemInstructions: activePrompt?.versions ? activePrompt?.versions[0]?.systemInstructions : '',
    //   //   templateBody: activePrompt?.versions ? activePrompt?.versions[0]?.templateBody : '',
    //   //   "uiRenderingType": "Markdown",
    //   //   modelIds: activePrompt?.selectedModels
    //   // }).then((res) => {



    //   // })

    //   // await new Promise(r => setTimeout(r, 700 + Math.random() * 800));

    //   // let mockData;
    //   // if (activePrompt.uiType === 'TABLE') {
    //   //   mockData = [
    //   //     { Topic: 'Technical', Question: 'How do you handle React performance?' },
    //   //     { Topic: 'Culture', Question: 'Describe a difficult team dynamic.' },
    //   //     { Topic: 'Growth', Question: 'Where do you see design systems in 2 years?' }
    //   //   ];
    //   // } else if (activePrompt.uiType === 'CHART') {
    //   //   mockData = [{ label: 'Fit', value: 85 }, { label: 'Skills', value: 70 }, { label: 'Exp', value: 95 }];
    //   // } else if (activePrompt.title === 'Screening Question Generator') {
    //   //   mockData = { text: `### Section 1: Strongly Recommended Questions (Ranked 1–6)\n\n**1. Do you have at least 5 years of commercial experience specifically with React.js?**\n*Justification: The role explicitly requires 5+ years of React experience as a core competency for a Senior level position.*\n\n**2. Have you implemented strict WCAG 2.1 compliance in a previous production application?**\n*Justification: Compliance with WCAG 2.1 is listed as a "strict compliance" requirement in the JD, indicating a non-negotiable legal or quality standard.*\n\n**3. Do you have experience optimizing the performance of large-scale React applications using hooks and memoization?**\n*Justification: Performance optimization and "deep knowledge of hooks" are highlighted as key technical responsibilities.*\n\n**4. Have you used Redux for state management in a professional project?**\n*Justification: Redux is listed as a mandatory skill ("Must have experience with Redux") for this backend-heavy frontend role.*\n\n**5. Do you have professional experience integrating React frontends with Node.js backends?**\n*Justification: Node.js experience is a specific requirement mentioned in the description.*\n\n**6. Are you available to work onsite/hybrid as per the company policy?**\n*Justification: Evaluating "role fit" and logistics is critical for initial screening to ensure availability matches company expectations.*\n\n### Section 2: Recommended Questions (Ranked 7–12)\n\n7. Have you ever migrated a legacy codebase to modern React hooks?\n8. Do you have experience writing unit tests for React components using Jest or React Testing Library?\n9. Have you worked in an Agile/Scrum environment previously?\n10. Are you comfortable conducting code reviews for junior developers?\n11. Do you have a notice period of less than 30 days?\n12. Have you worked with TypeScript in a React environment?` };
    //   // } else if (activePrompt.title === 'Work Style & Personality Analysis') {
    //   //   mockData = { text: `• **Adaptability** - Frequently shifted between frontend frameworks (Angular to React) and backend integration, indicating a high willingness to learn and adapt to changing technical requirements.\n\n• **Collaboration Style** - Mentioned "pairing with designers" and "mentoring juniors" in 3 separate roles, suggesting a highly collaborative, team-first approach rather than solitary coding.\n\n• **Task Ownership** - Use of action verbs like "spearheaded," "architected," and "owned" regarding the payment module migration demonstrates a strong capacity for end-to-end responsibility.\n\n• **Continuous Learning** - Consistent completion of certifications (AWS, WCAG) alongside full-time work shows a proactive approach to skill expansion and self-improvement.\n\n• **Problem-Solving Approach** - Description of "reducing load times by 40%" highlights a results-oriented mindset that focuses on measurable optimization rather than just feature delivery.` };
    //   // } else {
    //   //   mockData = { text: `Final analysis for ${activeCandidate?.name || 'the candidate'} regarding the ${activeJob?.title} role completed by ${modelId}.` };
    //   // }


    //   // setResults(prev => ({
    //   //   ...prev,
    //   //   [modelId]: { loading: false, data: mockData }
    //   // }));
    // }
    setExecuting(false);
  };


  const handlePublish = () => {

    if (!activePrompt?.result) return

    if (!activePrompt?.isWinner) {

      setShowModelSelector(true);
      return;

    }

    setPendingModelId(selectedWinnerId);
    setShowConfirmModal(true);
  };


  const finalizePublish = (winnerId) => {


    PostCallWithErrorResponse(ApiConfig.GENERATE_PROMPT_RESPONSE, {
      promptId: activePrompt.id,
      systemInstructions: activePrompt?.versions ? activePrompt?.versions[0]?.systemInstructions : '',
      templateBody: activePrompt?.versions ? activePrompt?.versions[0]?.templateBody : '',
      "uiRenderingType": "Markdown",
      modelIds: [activePrompt?.isWinner]
    }).then((res) => {

      if (res?.json?.succeded) {

        triggerToast('Success! Prompt is now LIVE.', 'success')

        // triggerToast(
        //   winningModel ? `Success! Prompt is now LIVE with model: ${winningModel.name}.` : 'Success! Prompt is now LIVE.'
        //   , 'success'
        // );
      }

    })


  };


  const triggerToast = (msg, type = 'success') => {
    setShowToast({ msg, type });
    setTimeout(() => setShowToast(null), 3000);
  };



  const handleGenerateResponse = () => {

    let input_variables = {}
    activePrompt?.variables?.forEach((i) => {
      input_variables[i?.name] = i?.defaultValue
    })

    console.log(input_variables)

    // PostCallWithErrorResponse(ApiConfig.GENERATE_PROMPT_RESPONSE, {
    //   input_variables
    // }).then((res) => {



    // })

  }



  return (
    <div className="main-wrapper overflow-hidden d-flex flex-column position-relative bg-light" style={{ height: '90vh' }}>

      {/* Shell: Header - Fixed flex-shrink-0 to prevent vertical squishing */}
      <header className="ds-header shadow-sm flex-shrink-0 bg-white">
        <div className="d-flex align-items-center gap-2">
          <div className="bg-primary rounded p-1 d-inline-flex align-items-center justify-content-center" style={{ width: '24px', height: '24px' }}>
            <span className="text-white fw-bold" style={{ fontSize: 'var(--font-size-sm)' }}>AI</span>
          </div>
          <h4 className="m-0 text-base fw-bold">Prompt Workbench</h4>
        </div>

        <ul className="nav-tabs-horizontal">
          <li className="nav-item-h active">Editor & Execution</li>
        </ul>
      </header>


      {/* Shell: Content Layout Wrapper */}
      <div className="d-flex flex-row flex-grow-1 overflow-hidden w-100">

        {/* Left Sidebar: Fixed width, flex-shrink-0 to prevent squishing */}
        <aside className="bg-white border-end d-flex flex-column flex-shrink-0" style={{ width: '280px' }}>
          <div className="p-3 border-bottom d-flex justify-content-between align-items-center bg-light flex-shrink-0">

            <span className="text-base fw-bold text-uppercase text-muted">Prompts</span>

            <button
              className="btn btn-icon btn-sm"
              title="New Prompt"
              onClick={() => setShowFeatureModal(true)}
            >
              +
            </button>
          </div>
          <div className='p-2'>

            <Form.Control
              style={{ width: "98%" }}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder='Search'
              className="input-text-14"
            />
          </div>
          {/* Scrollable inner container for sidebar items ONLY */}
          <div className="p-2 flex-grow-1" style={{ overflowY: 'auto', overflowX: 'hidden' }}>
            {suggestedPrompt?.filter((p) => {
              return search.toLowerCase() === '' ? true : p.friendlyName.toLowerCase().includes(search.toLowerCase())
            })?.map(p => (
              <div
                key={p.id}
                onClick={() => {
                  let update = activePrompt;
                  setSuggestedPrompt(prev =>
                    prev.map(item =>
                      activePrompt.id == item.id
                        ? update
                        : item
                    )
                  )

                  if (selectedJobId) {

                    getDefualtValueForvar(selectedJobId, selectedCandidateId || 0)
                  }
                  console.log(suggestedPrompt);
                  setActivePrompt(p)





                  setSelectedWinnerId(null)
                }}
                className={`card-interactive card-std p-2 mb-2 border ${activePrompt.id === p.id ? 'selected shadow-sm' : ''}`}
                style={{ marginBottom: '8px', height: '70px' }} // overriding custom css margins to keep it tight
              >
                <div className={`fw-bold text-sm mb-1 text-truncate ${activePrompt.id === p.id ? 'text-primary' : 'text-dark'}`}>{p.friendlyName}</div>
                <div className="text-base text-muted text-truncate">{p?.versions[0]?.templateBody}</div>
                {p.winnerModelId && (
                  <div className="mt-2 badge bg-success bg-opacity-10 text-success border border-success px-2 py-1 rounded-pill" style={{ fontSize: 'var(--font-size-sm)' }}>
                    Active: {AVAILABLE_MODELS.find(m => m.id === p.winnerModelId)?.name}
                  </div>
                )}
              </div>
            ))}
          </div>
        </aside>


        {/* Main Work Area: Scrolls as a single cohesive unit */}
        <main className="flex-grow-1 p-2" style={{ overflowY: 'auto', overflowX: 'hidden', backgroundColor: 'var(--ui-gray-bg)' }}>
          <div className="row g-2">

            {/* Left Configuration Column */}
            <div className="col-xl-6 d-flex flex-column gap-2">

              {/* Context Selector Card */}
              <div className="card-std border-0 shadow-sm p-3 bg-white m-0">
                <h6 className="text-base fw-bold text-uppercase text-muted mb-2">Dynamic Input Context</h6>
                <div className="row g-2">
                  <div className="col-md-6">
                    <label className="form-label text-base fw-bold">Active Job Code</label>
                    <CustomSelect
                      options={codeList}
                      valKey={'code'}
                      labelKey={'code'}
                      value={selectedJobId}
                      handleChange={(e) => {

                        console.log(e);

                        setSelectedJobId(e);
                        setSelectedCandidateId('');
                        candidateList(e)
                        getDefualtValueForvar(e)
                      }}
                      isClearaAble={false}
                    />
                    {/* <AutocompleteSelect
                      label="Active Job Code"
                      options={codeList.map(j => ({ value: j?.code, label: `${j.code} - ${j.designation}` }))}
                      value={selectedJobId}
                      onChange={(val) => {
                        setSelectedJobId(val);
                        setSelectedCandidateId('');
                        candidateList(val)
                      }}
                      placeholder="Type to search..."
                    /> */}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-base fw-bold">Select Candidate</label>
                    <CustomSelect
                      options={candidates}
                      valKey={'userId'}
                      labelKey={'name'}
                      value={selectedCandidateId}
                      handleChange={(e) => {

                        getDefualtValueForvar(selectedJobId, e)
                        setSelectedCandidateId(e);
                      }}
                      isClearaAble={false}
                    />
                    {/* <AutocompleteSelect
                      label="Select Candidate"
                      options={candidates.map(c => ({ value: c.id, label: c.name }))}
                      value={selectedCandidateId}
                      onChange={setSelectedCandidateId}
                      placeholder="Type to search..."
                      disabled={!selectedJobId}
                    /> */}
                  </div>
                </div>
                {activeJob && (
                  <div className="mt-2 p-2 bg-light border rounded">
                    <div className="text-base text-muted mb-1 fw-bold">JOB DESCRIPTION (DYNAMIC):</div>
                    <div className="text-base text-dark opacity-75" style={{ maxHeight: '60px', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: '3', WebkitBoxOrient: 'vertical' }}>{activeJob.description}</div>
                  </div>
                )}
              </div>


              {/* Editor Card */}
              <div className="card-std border-0 shadow-sm p-3 bg-white m-0">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <h4 className="font-size-h3 m-0 fw-bold">Logic Configuration</h4>
                </div>
                <label className="form-label text-base fw-bold text-uppercase text-muted">System Instructions</label>
                <div className="mb-3">

                  <div className="text-base text-info mb-1">Template Body</div>
                  <textarea
                    className="form-control font-monospace text-sm shadow-none"
                    rows="4"
                    style={{ resize: 'vertical', backgroundColor: '#fafafa' }}
                    value={activePrompt?.versions && activePrompt?.versions[0]?.templateBody || ''}
                    onChange={(e) => setActivePrompt(prev => ({
                      ...prev,
                      versions: prev.variables.map((versions, indx) =>
                        indx === 0
                          ? { ...versions, templateBody: e.target.value }
                          : versions
                      )
                    }))
                    }
                  ></textarea>
                </div>
                {
                  console.log(activePrompt?.variables)
                }
                {



                  activePrompt?.variables?.map((i, index) => {

                    return <div className="mb-3">

                      <div className="text-base text-info mb-1">Use tags like <code>{`{${i?.name}}`}</code></div>
                      <textarea
                        className="form-control font-monospace text-sm shadow-none"
                        rows="4"
                        style={{ resize: 'vertical', backgroundColor: '#fafafa' }}
                        value={i?.defaultValue || ''}
                        onChange={(e) => setActivePrompt(prev => ({
                          ...prev,
                          variables: prev.variables.map((variables, indx) =>
                            index === indx
                              ? { ...variables, defaultValue: e.target.value }
                              : variables
                          )
                        }))
                        }



                      ></textarea>
                    </div>

                  })
                }




                <div className="row g-2 p-2 bg-light rounded border mb-3">
                  {/* <div className="col-md-6">
                    <label className="form-label text-base fw-bold text-muted">UI Rendering Type</label>
                    <select
                      className="form-select text-sm shadow-none"
                      value={activePrompt.uiType}
                      onChange={(e) => updateActivePrompt({ uiType: e.target.value })}
                    >
                      {COMPONENT_TYPES.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </div> */}
                  <div className="col-md-12">
                    <label className="form-label text-base fw-bold text-muted">Model Test Suite</label>
                    <div className="d-flex flex-wrap gap-2 pt-1">
                      {aiModels?.map(m => (
                        <div key={m.id} className="form-check m-0">
                          <input
                            className="form-check-input shadow-none"
                            type="checkbox"
                            id={`chk-${m.id}`}
                            checked={activePrompt.selectedModels && activePrompt.selectedModels.includes(m.id)}
                            onChange={(e) => {
                              const newModels = e.target.checked
                                ? [...activePrompt.selectedModels || [], m.id]
                                : activePrompt?.selectedModels?.filter(id => id !== m.id);
                              updateActivePrompt({ selectedModels: newModels });


                            }}
                          />
                          <label className="form-check-label text-base" htmlFor={`chk-${m.id}`}>{m.modelName}</label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>


                <div className="d-flex gap-2">
                  <button
                    className="btn btn-primary flex-grow-1 py-1 text-sm fw-bold shadow-sm"
                    onClick={handleExecute}
                    disabled={executing
                      || !activePrompt?.selectedModels || activePrompt?.selectedModels?.length === 0
                      || !selectedCandidateId ||
                      activePrompt?.isPromptLoading ||
                      activePrompt?.variables?.some(v => !v.defaultValue?.trim())

                    }
                  >
                    {!selectedCandidateId ? 'Select Candidate to Run' : executing ? 'Executing...' : 'Run Analysis'}
                  </button>
                  <button
                    className={`btn py-1 text-sm fw-bold px-4 shadow-sm ${activePrompt?.isWinner ? 'btn-success' : 'btn-outline-primary'}`}
                    onClick={handlePublish}
                    title={!activePrompt?.isWinner ? "Publish logic for production use" : "Save this configuration"}
                  >
                    {activePrompt?.isWinner ? `Publish ${aiModels && aiModels.find(m => m.id === activePrompt?.isWinner)?.modelName}` : 'Publish Prompt'}
                  </button>
                </div>
              </div>
            </div>


            {/* Right Results Column */}
            <div className="col-xl-6 d-flex flex-column" >
              <div className="mb-2">
                <h6 className="text-base fw-bold text-uppercase text-muted m-0">Model Test Suite</h6>
              </div>


              {activePrompt?.selectedModels?.length === 0 ? (
                <div className="information-card w-100 p-3">
                  Select at least one model.
                </div>
              ) : (
                <>
                  <div className="nav-tabs-custom overflow-auto mb-2" style={{ gap: '16px' }}>
                    {activePrompt?.selectedModels?.map(modelId => {

                      // console.log(modelId);
                      const model = aiModels.find(m => m.id === modelId);

                      // console.log(model);
                      return (
                        <button
                          key={modelId}
                          className={`nav-link-custom ${activeResultTab === modelId ? 'active fw-bold' : ''} text-nowrap`}
                          onClick={() => setActiveResultTab(modelId)}
                        >
                          {model?.modelName}
                        </button>
                      );
                    })}
                  </div>


                  {activeResultTab && (
                    <div className="d-flex flex-column gap-2 pb-3" >
                      {(() => {
                        const modelId = activeResultTab;

                        console.log(modelId, activePrompt);

                        const model = aiModels && aiModels?.find(m => m?.id === modelId);
                        const result = activePrompt?.result && activePrompt?.result[model?.modelName];

                        console.log(result);

                        const isWinner = selectedWinnerId === modelId;

                        return (
                          <div
                            key={modelId}
                            className={`card-std p-0 bg-white overflow-hidden shadow-sm border ${activePrompt?.isWinner == modelId ? 'border-success border-2' : ''}`}
                            style={activePrompt?.isWinner == modelId ? { boxShadow: '0 0 0 4px rgba(25, 135, 84, 0.15)' } : {}}
                          >
                            <div className={`card-header p-3 border-bottom d-flex justify-content-between align-items-center ${isWinner ? 'bg-success bg-opacity-10' : 'bg-white'}`}>
                              <div className="d-flex align-items-center gap-2">
                                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: result?.loading ? '#fbbf24' : '#10b981' }}></div>
                                <span className="text-sm fw-bold text-dark">{model?.name}</span>
                                {activePrompt?.isWinner == modelId && <span className="badge bg-success text-white" style={{ fontSize: 'var(--font-size-sm)' }}>SELECTED</span>}
                              </div>
                              <span className="text-base text-muted">{model?.provider}</span>
                            </div>

                            <div className="p-0 position-relative">
                              <RenderResult
                                type={activePrompt.uiType}
                                data={result}
                                isLoading={activePrompt?.isPromptLoading}
                              />
                            </div>


                            {!activePrompt?.isPromptLoading && result && (
                              <div className="p-2 border-top bg-light d-flex justify-content-end">
                                <button
                                  className={`btn shadow-sm py-1 ${activePrompt?.isWinner == modelId ? 'btn-success' : 'btn-outline-secondary bg-white'} text-base`}
                                  onClick={() =>
                                    setActivePrompt(prev => ({
                                      ...prev,
                                      isWinner: modelId
                                    }))
                                  }
                                >
                                  {activePrompt?.isWinner == modelId ? '✓ Selected for Production' : 'Select this Output'}
                                </button>
                              </div>
                            )
                            }
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </main>
      </div >


      {/* Model Selection Modal */}
      {
        showModelSelector && (
          <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999 }} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content shadow-lg border-0">
                <div className="modal-header border-bottom">
                  <h4 className="modal-title text-base fw-bold">Select Default Model</h4>
                  <button type="button" className="btn-close shadow-none" onClick={() => setShowModelSelector(false)}></button>
                </div>
                <div className="modal-body p-4 bg-light">
                  <p className="text-sm text-muted mb-3">You have multiple model results. Please select which model's output should be the default configuration before publishing:</p>
                  <div className="list-group shadow-sm">
                    {activePrompt?.selectedModels?.map(modelId => {
                      const model = aiModels.find(m => m.id === modelId);
                      return (
                        <button
                          key={modelId}
                          className="list-group-item list-group-item-action d-flex justify-content-between align-items-center p-3 border-start-0 border-end-0 border-top-0 border-bottom"
                          onClick={() => {
                            // setSelectedWinnerId(modelId);
                            setActivePrompt(prev => ({
                              ...prev,
                              isWinner: modelId
                            }))
                            setShowModelSelector(false);
                            setPendingModelId(modelId);
                            setShowConfirmModal(true);
                          }}
                        >
                          <span className="text-sm fw-bold text-dark">{model?.modelName}</span>
                          <span className="text-base text-muted badge bg-light text-dark border">{model?.provider}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }


      {/* Confirmation Modal */}
      {
        showConfirmModal && (
          <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 10000 }} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content shadow-lg border-0">
                <div className="modal-header border-bottom">
                  <h4 className="modal-title text-base fw-bold text-danger">Confirm Publish</h4>
                  <button type="button" className="btn-close shadow-none" onClick={() => setShowConfirmModal(false)}></button>
                </div>
                <div className="modal-body p-4 text-center">
                  <div className="mb-3 text-warning">
                    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
                    </svg>
                  </div>
                  <p className="text-sm text-dark mb-1 fw-bold">This prompt will be pushed to live.</p>
                  <p className="text-base text-muted mb-0">Kindly ensure you want to make these prompt changes live.</p>
                </div>
                <div className="modal-footer bg-light border-top-0 justify-content-between p-2 rounded-bottom">
                  <button type="button" className="btn btn-outline-secondary btn-sm px-3 fw-bold" onClick={() => setShowConfirmModal(false)}>Cancel</button>
                  <button type="button" className="btn btn-danger btn-sm px-4 fw-bold shadow-sm" onClick={() => {
                    finalizePublish(pendingModelId);
                    setShowConfirmModal(false);
                  }}>Make Live</button>
                </div>
              </div>
            </div>
          </div>
        )
      }


      {/* Feature Coming Soon Modal */}
      {
        showFeatureModal && (
          <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999 }} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content shadow-lg border-0">
                <div className="modal-header border-bottom">
                  <h4 className="modal-title text-base fw-bold">Coming Soon</h4>
                  <button type="button" className="btn-close shadow-none" onClick={() => setShowFeatureModal(false)}></button>
                </div>
                <div className="modal-body p-4 text-center">
                  <div className="mb-3 text-primary opacity-75">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
                      <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0M7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0z" />
                    </svg>
                  </div>
                  <p className="text-sm text-muted mb-0">Add new prompt facility is coming soon!</p>
                </div>
                <div className="modal-footer bg-light border-top-0 justify-content-center rounded-bottom p-2">
                  <button type="button" className="btn btn-primary btn-sm px-4" onClick={() => setShowFeatureModal(false)}>Got it</button>
                </div>
              </div>
            </div>
          </div>
        )
      }


      {/* Shell: Feedback Toast */}
      {
        showToast && (
          <div className="position-fixed bottom-0 end-0 p-4" style={{ zIndex: 10000 }}>
            <div className={`toast show align-items-center text-white ${showToast.type === 'warning' ? 'bg-warning text-dark' : 'bg-dark'} border-0 shadow-lg`}>
              <div className="d-flex p-2">
                <div className="toast-body text-base fw-bold d-flex align-items-center gap-2">
                  <span>{showToast.type === 'success' ? '✔' : '⚠'}</span> {showToast.msg}
                </div>
                <button type="button" className={`btn-close shadow-none ${showToast.type === 'warning' ? '' : 'btn-close-white'} ms-auto me-2`} onClick={() => setShowToast(null)}></button>
              </div>
            </div>
          </div>
        )
      }


      {/* EMBEDDED STYLES FROM ATTACHED CSS */}
      <style>{`
        :root {
          --brand-primary: #081DA5;
          --brand-bg: #f5f8ff;
          --brand-text: #212529;
          --color-info-bg: #eef2ff;
          --color-success-bg: #d1e7dd; --color-success-text: #198754;
          --color-warning-bg: #fff3cd; --color-warning-text: #856404;
          --color-danger-bg: #f8d7da; --color-danger-text: #dc3545;
          --ui-gray-bg: #F2F4F7;
          --ui-border: #d0d5dd;
          --ui-text-muted: #667085;
          --ui-text-dark: #344054;
          --radius-md: 0.5rem;
          --radius-lg: 0.75rem;
          --bs-primary: var(--brand-primary);
          --bs-body-font-family: 'Inter', sans-serif;
          --bs-body-bg: var(--ui-gray-bg);
          --bs-border-radius: var(--radius-md);
        }


        body {
          font-family: var(--bs-body-font-family) !important;
          font-size: var(--font-size-h4);
          background-color: var(--bs-body-bg);
          margin: 0; padding: 0;
        }


        /* Essential Scrollbar Formatting overrides for deployment */
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #D0D5DD; border-radius: 8px; }
        ::-webkit-scrollbar-thumb:hover { background: #98A2B3; }


        // .main-wrapper { background-color: var(--brand-bg); min-height: 100vh; display: flex; flex-direction: column; }
       
        .ds-header {
          background: #fff; border-bottom: 1px solid var(--ui-border);
          position: sticky; top: 0; z-index: 1000;
          display: flex; flex-direction: row; justify-content: space-between; align-items: center;
          padding: 0 1.5rem;
          height: 48px;
        }
        .nav-tabs-horizontal {
          display: flex; gap: 2rem;
          border-bottom: none;
          margin: 0; padding: 0;
          list-style: none;
          height: 100%;
        }
        .nav-item-h {
          padding: 0 0.25rem;
          display: flex; align-items: center;
          cursor: pointer;
          color: var(--ui-text-muted);
          font-weight: 500;
          font-size: 14px;
          border-bottom: 2px solid transparent;
          transition: all 0.2s;
          height: 100%;
        }
        .nav-item-h.active {
          color: var(--brand-primary);
          border-bottom-color: var(--brand-primary);
        }


      

        .form-control, .form-select {
          color: #344054; font-size: 14px; font-weight: 500;
          border-radius: 8px; min-height: 34px; border: 1px solid var(--ui-border);
          background: #fff;
        }


        .card-std {
          background: #fff; border: 1px solid var(--ui-border); border-radius: 12px;
          transition: 0.3s; position: relative;
        }
        .card-interactive {
          cursor: pointer; transition: all 0.2s; border: 1px solid transparent;
        }
        .card-interactive:hover {
          border-color: var(--brand-primary) !important; background-color: #f8faff;
          transform: translateY(-2px);
        }
        .card-interactive.selected {
          border-color: var(--brand-primary) !important; background-color: var(--color-info-bg);
          box-shadow: 0 0 0 1px var(--brand-primary) inset;
        }


        .information-card {
          display: inline-flex; align-items: center; justify-content: center;
          padding: 4px 12px;
          border-radius: 6px; background-color: var(--color-warning-bg);
          color: var(--color-warning-text); border: 1px solid var(--color-warning-text);
          font-size: 11px; font-weight: 700;
        }
       
        .nav-tabs-custom { display: flex; border-bottom: 1px solid var(--ui-border); margin-bottom: 16px; gap: 24px; }
        .nav-link-custom {
          padding: 10px 4px; color: var(--ui-text-muted); font-weight: 500; font-size: 14px;
          background: none; border: none; border-bottom: 2px solid transparent; cursor: pointer; transition: 0.2s;
        }
        .nav-link-custom:hover { color: var(--brand-text); }
        .nav-link-custom.active { color: var(--brand-primary); border-bottom-color: var(--brand-primary); }


        .text-xs { font-size: 0.75rem !important; }
        .text-sm { font-size: 0.875rem !important; }
        .font-monospace { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace !important; }
       
        .border-dashed { border-style: dashed !important; border-width: 2px !important; }
      `}</style>
    </div >
  );
}

